<script setup lang="ts">
  import JSONEditor from 'jsoneditor'
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { CheckCircle2 } from 'lucide-vue-next'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { TableSchema } from '../models/TableSchema'
  import { TableJsonEditorSchema } from '../services/TablesJsonTableConfigurationService'
  import { TablesService } from '../services/TablesService'
  import { TablesTypeGuardService } from '../services/TablesTypeGuardService'

  interface IJsonTableConfigurationProps {
    tableSchemas: Array<TableSchema>
    availableColumns: Array<{ value: string; label: string }>
    modelValue: {}
    placeHolder?: string
    configurationLevel: number
    editorSchema: TableJsonEditorSchema
    allowTypeChange: boolean
  }

  interface IJsonTableConfigurationEmits {
    (e: 'update:modelValue', value: {}): void
    (e: 'table-type-changed', type: string, configuration: {}): void
  }

  const props = defineProps<IJsonTableConfigurationProps>()
  const emit = defineEmits<IJsonTableConfigurationEmits>()

  const modelValue = ref<{}>({ ...props.modelValue })

  watch(
    modelValue,
    (newVal) => {
      emit('update:modelValue', newVal)
    },
    { deep: true }
  )

  const newApplyableTableType = ref<string | null>(null)

  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        schema: props.editorSchema,
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          try {
            const newVal = editor.get()
            if (props.allowTypeChange === true) {
              onEditorChangeForTable(newVal)
            } else {
              onEditorChangeForField(newVal)
            }
          } catch (err: any) {
            const editorValue = editor.getText?.() ?? ''
            if (editorValue.trim() === '') {
              message.error('Editor content cannot be empty.', defaultMessageDurationSeconds)
            }
          }
        }
      })
      setJSONEditorModel(modelValue.value)
    }
  }

  const onEditorChangeForTable = (editorValue: any) => {
    newApplyableTableType.value = null
    if (typeof editorValue === 'object') {
      if (editorValue.type != null) {
        const tableType = TablesService.tryGetTableSchemaByType(
          editorValue.type,
          props.tableSchemas
        )
        if (
          tableType != null &&
          tableType.type !== TablesService.CUSTOM_TABLE_TYPE &&
          (props.configurationLevel === 1 ||
            !TablesTypeGuardService.supportsChildTables(tableType))
        ) {
          newApplyableTableType.value = tableType.type
        }
      }
      modelValue.value = editorValue
    }
  }

  const onEditorChangeForField = (editorValue: any) => {
    modelValue.value = editorValue
  }

  const setJSONEditorModel = (value: {}) => {
    if (editor != null) {
      newApplyableTableType.value = null
      editor.set(value)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  const onApplyNewTableType = () => {
    if (newApplyableTableType != null) {
      emit('table-type-changed', newApplyableTableType.value as string, modelValue)
    }
  }

  onMounted(() => {
    initializeJSONEditor()
  })

  onUnmounted(() => {
    if (editor) {
      editor.destroy()
    }
  })
</script>

<template>
  <div class="flex flex-col">
    <div v-if="props.allowTypeChange === true" class="mb-3 flex justify-end">
      <a-tooltip>
        <template #title>
          {{ `Apply Table type: ${newApplyableTableType}` }}
        </template>
        <Button type="primary" :disabled="newApplyableTableType == null" @click="onApplyNewTableType">
          <span class="ant-button-flex-content">
            <CheckCircle2 class="h-4 w-4" />
            {{ 'Apply Table Type' }}
          </span>
        </Button>
      </a-tooltip>
    </div>
    <div ref="editorContainer" style="min-height: 500px; height: 500px"></div>
  </div>
</template>
